import { useQuery } from "@tanstack/react-query";
import { navigate } from "raviger";
import { useTranslation } from "react-i18next";

import { Card, CardContent } from "@/components/ui/card";

import { getPermissions } from "@/common/Permissions";
import Page from "@/components/Common/Page";
import { QuestionnaireForm } from "@/components/Questionnaire/QuestionnaireForm";
import { EmptyState } from "@/components/ui/empty-state";
import { usePermissions } from "@/context/PermissionContext";

import useAppHistory from "@/hooks/useAppHistory";

import query from "@/Utils/request/query";
import {
  PatientDeceasedInfo,
  PatientHeader,
} from "@/components/Patient/PatientHeader";
import {
  getEncounterPermissionScopes,
  getRequiredEncounterQuestionnaireScope,
} from "@/pages/Encounters/utils/encounterPermissions";
import encounterApi from "@/types/emr/encounter/encounterApi";
import { ShieldAlert } from "lucide-react";

interface Props {
  facilityId?: string;
  patientId: string;
  encounterId?: string;
  questionnaireSlug?: string;
  subjectType?: string;
}

export default function EncounterQuestionnaire({
  facilityId,
  patientId,
  encounterId,
  questionnaireSlug,
  subjectType,
}: Props) {
  const { t } = useTranslation();

  const { goBack } = useAppHistory();
  const { hasPermission } = usePermissions();
  const { data: encounter, isLoading: isEncounterLoading } = useQuery({
    queryKey: ["encounter", encounterId],
    queryFn: query(encounterApi.get, {
      pathParams: { id: encounterId ?? "" },
      queryParams: { facility: facilityId! },
    }),
    enabled: !!encounterId,
  });
  const encounterPermissions = getPermissions(
    hasPermission,
    encounter?.permissions ?? [],
  );
  const encounterScopes = getEncounterPermissionScopes({
    facilityId,
    selectedEncounterId: encounterId,
    primaryEncounterId: encounterId,
    selectedEncounterStatus: encounter?.status,
    primaryEncounterStatus: encounter?.status,
    selectedEncounterPermissions: encounterPermissions,
    primaryEncounterPermissions: encounterPermissions,
    patientPermissions: getPermissions(
      hasPermission,
      encounter?.patient.permissions ?? [],
    ),
  });
  const requiredEncounterScope =
    getRequiredEncounterQuestionnaireScope(questionnaireSlug);
  const canRenderEncounterQuestionnaire =
    !encounterId ||
    (requiredEncounterScope === "details" &&
      encounterScopes.canWriteSelectedEncounter) ||
    (requiredEncounterScope === "clinical" &&
      encounterScopes.canWriteClinicalData) ||
    (requiredEncounterScope === "questionnaire" &&
      encounterScopes.canSubmitEncounterQuestionnaire);

  if (encounterId && isEncounterLoading) {
    return null;
  }

  return (
    <Page
      title={t("questionnaire_one")}
      className="block md:px-1 -mt-4"
      hideTitleOnPage
    >
      <div className="flex flex-col space-y-4">
        {encounter && (
          <div className="flex flex-col gap-2">
            <PatientHeader
              patient={encounter.patient}
              facilityId={facilityId}
              className="bg-white shadow-sm rounded-sm"
            />
            <PatientDeceasedInfo patient={encounter.patient} />
          </div>
        )}
        <Card className="mt-2">
          <CardContent className="lg:p-4 p-0">
            {!canRenderEncounterQuestionnaire ? (
              <EmptyState
                icon={<ShieldAlert className="text-gray-400 size-8" />}
                title={t("no_permission_to_view_page")}
                description={t("permission_denied_encounter")}
                className="min-h-80 bg-transparent"
              />
            ) : (
              <QuestionnaireForm
                facilityId={facilityId}
                patientId={patientId}
                subjectType={subjectType}
                encounterId={encounterId}
                questionnaireSlug={questionnaireSlug}
                onSubmit={() => {
                  if (encounterId && facilityId) {
                    navigate(
                      `/facility/${facilityId}/patient/${patientId}/encounter/${encounterId}/updates`,
                    );
                  } else if (facilityId) {
                    navigate(
                      `/facility/${facilityId}/patient/${patientId}/updates`,
                    );
                  } else {
                    navigate(`/patient/${patientId}/updates`);
                  }
                }}
                onCancel={() => goBack()}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </Page>
  );
}
